import i18next from "i18next";
import { useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { getFlightsMiniInfo } from "../../../info/allFlights.js";
import { getCities } from "../../../protocols";
import { setRoutes } from "../../../store/searchParamsSlice";
import { AirlineCardComponent } from "./AirlineCardComponent/index.jsx";
import { CitySectionComponent } from "./CitySectionComponent/index.jsx";
import { SimilarDirectionsComponent } from "./SimilarDirectionsComponent";
import { AirlinesGrid, Container, Header, InfoCard, Section, SectionTitle, Title } from "./style.js";

export const FlightsDirectionInfoComponent = ({ from, to }) => {
  const { t, language } = i18next;
  const dispatch = useDispatch();
  const { route } = useParams();
  const navigate = useNavigate();

  const [isLoading, setIsLoading] = useState(true);
  const [flightData, setFlightData] = useState(null);
  const [flightsList, setFlightsList] = useState([]);

  useEffect(() => {
    const getFlightData = async () => {
      try {
        const flightData = await import(`../../../info/flightsInfoJsons/${route}.json`);

        if (flightData?.default) {
          setFlightData(flightData.default);
          setIsLoading(false);
        } else {
          navigate("/");
        }
      } catch (error) {
        console.error("Error loading flight data:", error);
        navigate("/");
      }
    };

    if (route) {
      getFlightData();
    }
  }, [route, navigate]);

  useEffect(() => {
    if (from && to && flightData) {
      const fromCode = flightData?.cityCodes?.[from];
      const toCode = flightData?.cityCodes?.[to];

      const getCitiesFunction = async () => {
        if (!fromCode || !toCode) {
          console.log("No values of fromCode or toCode");
          return;
        }

        try {
          const [fromCities, toCities] = await Promise.all([getCities(fromCode), getCities(toCode)]);

          if (fromCities.length > 0 && toCities.length > 0) {
            const fromCityInfo = fromCities.find(city => city.code === fromCode);
            const toCityInfo = toCities.find(city => city.code === toCode);

            if (fromCityInfo && toCityInfo) {
              dispatch(
                setRoutes([
                  { from: fromCityInfo, to: toCityInfo, date: null },
                  { from: null, to: null, date: null },
                ])
              );
            }
          }
        } catch (error) {
          console.error("Error fetching cities:", error);
        }
      };

      getCitiesFunction();
    }
  }, [from, to, flightData, dispatch]);

  useEffect(() => {
    const getFlightsAll = async () => {
      try {
        const flights = await getFlightsMiniInfo(11);
        setFlightsList(flights);
      } catch (error) {
        console.error("Error fetching flights:", error);
      }
    };

    getFlightsAll();
  }, []);

  const content = useMemo(() => flightData?.content?.[language]?.content, [flightData, language]);

  if (isLoading) {
    return (
      <Container>
        <div>Loading...</div>
      </Container>
    );
  }

  return (
    <Container>
      {content?.map((item, index) => (
        <div key={index}>
          <Header>
            <Title>{item.routeArticleTitle}</Title>
          </Header>

          <Section>
            <SectionTitle>{item.cityInfo?.cityInfoTitleFrom}</SectionTitle>
            <CitySectionComponent
              title={item.cityInfo?.cityInfoTitleFrom}
              description={item.cityInfo?.cityInfoDescFrom}
              imageUrl={item.cityInfo?.cityInfoDescFromImgUrl}
              altText={`${from} image`}
            />
          </Section>

          <Section>
            <SectionTitle>{item.cityInfo?.cityInfoTitleTo}</SectionTitle>
            <CitySectionComponent
              title={item.cityInfo?.cityInfoTitleTo}
              description={item.cityInfo?.cityInfoDescTo}
              imageUrl={item.cityInfo?.cityInfoDescToImgUrl}
              altText={`${to} image`}
            />
          </Section>

          <Section>
            <SectionTitle>{item.flightInfo?.flightInfoTitle}</SectionTitle>
            <InfoCard dangerouslySetInnerHTML={{ __html: item.flightInfo?.flightInfoDesc }} />
          </Section>

          <Section>
            <SectionTitle>{item.flightInfo?.popularAirlinesTitle}</SectionTitle>
            <AirlinesGrid>
              {item?.flightInfo?.popularAirlines?.map((flightInfo, index) => {
                return <AirlineCardComponent key={index} flightInfo={flightInfo} />;
              })}
            </AirlinesGrid>
          </Section>

          <Section>
            <SectionTitle>{item.flightInfo?.whenToFlyTitle}</SectionTitle>
            <InfoCard>
              <p dangerouslySetInnerHTML={{ __html: item.flightInfo?.whenToFlyDesc }} />
            </InfoCard>
          </Section>
        </div>
      ))}

      <Section>
        <SectionTitle>{t("same_routes")}</SectionTitle>
        <SimilarDirectionsComponent flightsList={flightsList} currentRoute={route} language={language} />
      </Section>
    </Container>
  );
};
